import RootAdmin from "../../../../components/layouts/RootAdmin";
import { Button, ButtonGroup, Card, CardBody, CardHeader, Chip, useDisclosure, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Checkbox, Input, Divider, Spinner } from "@nextui-org/react";
import { ArrowPathIcon, PlusIcon, LinkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import FilterReactSelect from "../../../../components/elements/FilterReactSelect";
import { getFetcher, postFetcher } from "../../../../services/api";
import useSWR from "swr";
import { useMemo, useState, useCallback } from "react";
import { formattedDate, hasValidRole, isResponseErrorObject } from "../../../../services/helpers";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import { USER_ADD_RECOMENDATION_PATH } from "../../../../services/routes";
import { useNavigate } from "react-router-dom";
import StatusRekomendasi from "../../../../enums/statusRekomendasi";
import toast from "react-hot-toast";
import ClientDataGrid from "../../../../components/elements/ClientDataGrid";
import { useSelectedCompany } from "../../../../hooks/useSelectedCompany";
import { calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan } from "../../../../services/helpers";
import TimeProcessBadge from "../../../../components/fragments/admin/recomendation/TimeProcessBadge";
import useListCompany from "../../../../hooks/useListCompany";

export default function IndexPage() {
  const { user, roles } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasValidRole(roles, [RolesAccess.SUPER_ADMIN]);
  const [isLoadingPtpsp, setIsLoadingPtpsp] = useState(false);
  const [selectedCompany, setSelectedCompany] = useSelectedCompany();
  const { dataCompany, isLoadingCompany } = useListCompany();

/* Error
  const apiEndpoint = useMemo(() => {
    if (isAdmin) return `/api/rekom/permohonan/search?returnAll=true`;
    if (selectedCompany) {
      return `/api/rekom/permohonan/search?companyId=${selectedCompany.value}&returnAll=true`;
    }
    return `/api/rekom/permohonan/search?companyId=${user.companies?.map((company) => company.companyId).join(",")}&returnAll=true`;
  }, [isAdmin, selectedCompany, user]);
  */
  const apiEndpoint = useMemo(() => {
    // 1. Prioritaskan pilihan dari dropdown (berlaku untuk Admin maupun User biasa)
    if (selectedCompany && selectedCompany.value) {
      return `/api/rekom/permohonan/search?companyId=${selectedCompany.value}&returnAll=true`;
    }
    
    // 2. Jika dropdown kosong (clear) dan user adalah Admin, tampilkan SEMUA data
    if (isAdmin) {
      return `/api/rekom/permohonan/search?returnAll=true`;
    }
    
    // 3. Jika dropdown kosong dan user BUKAN Admin, tampilkan data HANYA dari list perusahaannya
    const userCompanyIds = user.companies?.map((company) => company.companyId).join(",");
    return `/api/rekom/permohonan/search?companyId=${userCompanyIds}&returnAll=true`;
  }, [isAdmin, selectedCompany, user]);

  const { data, isLoading, mutate } = useSWR(apiEndpoint, getFetcher);

  // Modal state for SK selection
  const { isOpen: isSkModalOpen, onOpen: onSkModalOpen, onClose: onSkModalClose } = useDisclosure();
  const [parentSkList, setParentSkList] = useState([]);
  const [selectedSkIds, setSelectedSkIds] = useState([]);
  const [skSearchQuery, setSkSearchQuery] = useState("");
  const [isLoadingParentSk, setIsLoadingParentSk] = useState(false);
  const [isLinkingSk, setIsLinkingSk] = useState(false);
  const [parentCompanyName, setParentCompanyName] = useState("");

  // Confirmation modal state
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  // Get current company's idCompanySimpel
  const currentCompanyData = useMemo(() => {
    if (!selectedCompany || !dataCompany?.data) return null;
    return dataCompany.data.find((c) => c.id === selectedCompany.value);
  }, [selectedCompany, dataCompany]);

  const hasIdCompanySimpel = currentCompanyData?.idCompanySimpel;
  const hasPerusahaanInduk = currentCompanyData?.idPerusahaanInduk;

  const columns = useMemo(
    () => [
      { field: "kodePermohonan", headerName: "Nomor Registrasi" },
      { field: "tanggalPengajuan", headerName: "Tanggal Pengajuan", valueGetter: (value, row) => formattedDate(row.tanggalPengajuan) ?? "-" },
      { field: "nomorSurat", headerName: "Nomor Surat", valueGetter: (value, row) => row.draftSurat?.nomorSuratFormatted },
      { field: "company", headerName: "Perusahaan", valueGetter: (value, row) => row.company.name },
      { field: "tipeSurat", headerName: "Tipe Permohonan" },
      {
        field: "status",
        headerName: "Status",
        renderCell: (params) => (
          <Chip size="sm" color="primary" variant="flat">
            {params?.row?.status}
          </Chip>
        ),
      },
      {
        field: "lamaProses",
        headerName: "Lama Proses",
        renderCell: (params) => {
          const lamaProses = calculateRegistrasiRekomendasiProcessingDaysWithTanggalPengajuan(params.row.tanggal_pengajuan, params.row.tanggal_selesai);
          return lamaProses < 2 ? (
            <Chip color="success" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          ) : (
            <Chip color="danger" variant="flat" size="sm">
              {lamaProses} Hari
            </Chip>
          );
        },
      },
      { field: "nomorSuratPerusahaan", headerName: "Nomor Surat Perusahaan" },
      { field: "tanggalSuratPerusahaan", headerName: "Tanggal Surat Perusahaan", valueGetter: (value, row) => formattedDate(row.tanggalSuratPerusahaan) ?? "-" },
      { field: "nomorSuratRekomendasi", headerName: "Nomor Surat Rekomendasi" },
      { field: "tanggalSuratRekomendasi", headerName: "Tanggal Surat Rekomendasi", valueGetter: (value, row) => formattedDate(row.tanggalSuratRekomendasi) ?? "-" },
      { field: "tanggalSurat", headerName: "Tanggal Surat", valueGetter: (value, row) => formattedDate(row.draftSurat?.tanggalSurat) ?? "-" },
      { field: "tanggalSelesai", headerName: "Tanggal Selesai", valueGetter: (value, row) => formattedDate(row.tanggalSelesai) ?? "-" },
      { field: "tanggalBerlaku", headerName: "Tanggal Berlaku", valueGetter: (value, row) => formattedDate(row.tanggalBerlaku) ?? "-" },
      { field: "tanggalBerakhir", headerName: "Tanggal Berakhir", valueGetter: (value, row) => formattedDate(row.tanggalBerakhir) ?? "-" },
      { field: "identitasPemohon", headerName: "Identitas Pemohon", valueGetter: (value, row) => row.identitasPemohon?.namaPemohon ?? "-" },
      { field: "jumlahKendaraan", headerName: "Jumlah Kendaraan", valueGetter: (value, row) => row.vehicles?.length },
      { field: "bahanB3", headerName: "Jumlah Bahan B3", valueGetter: (value, row) => row.b3Substances?.length },
    ],
    [],
  );

  async function onValidateGetPermohonan() {
    try {
      setIsLoadingPtpsp(true);
      let userCom;
      if (user.companies.length === 0 && !hasValidRole(roles, [RolesAccess.SUPER_ADMIN])) {
        toast.error("Anda tidak memiliki perusahaan terdaftar!");
        setIsLoadingPtpsp(false);
        return;
      } else if (user.companies.length === 0 && hasValidRole(roles, [RolesAccess.SUPER_ADMIN])) {
        userCom = [];
      } else {
        userCom = user.companies.map((company) => company.company.kode_customer);
      }

      const data = {
        kode_kustomer: userCom,
        status: StatusRekomendasi.SELESAI,
        subLayanan: ["33", "32"],
      };
      await postFetcher(`/api/rekom/permohonan/get-from-ptsp-user`, data);
      mutate();
      toast.success("Berhasil Update List Permohonan Rekomendasi Pengakutan Dari PTSP!");
      setIsLoadingPtpsp(false);
    } catch (error) {
      isResponseErrorObject(error.response.data.message)
        ? Object.entries(error.response.data.message).forEach(([key, value]) => {
            toast.error(value);
          })
        : toast.error(error.response.data.message);
      setIsLoadingPtpsp(false);
    }
  }

  // Fetch parent company SK rekomendasi
  const fetchParentSkList = useCallback(async () => {
    if (!selectedCompany) {
      toast.error("Silakan pilih perusahaan terlebih dahulu");
      return;
    }

    setIsLoadingParentSk(true);
    try {
      const response = await getFetcher(`/api/rekom/permohonan/parent-company-sk/${selectedCompany.value}`);

      console.log("Response from API:", response);

      // Check if response is an error object (has statusCode, error, or message properties indicating error)
      if (response?.statusCode >= 400 || response?.error) {
        const errorMsg = response.message || "Gagal mengambil data SK rekomendasi dari perusahaan induk";
        toast.error(errorMsg);
        setParentSkList([]);
        setParentCompanyName("");
        return;
      }

      // Validate response has applications array
      if (response && response.applications && Array.isArray(response.applications)) {
        setParentSkList(response.applications);
        setParentCompanyName(response.parentCompany?.name || "");
        setSelectedSkIds([]);
        setSkSearchQuery("");
      } else {
        toast.error("Format data tidak valid");
        setParentSkList([]);
        setParentCompanyName("");
      }
    } catch (error) {
      console.error("Error fetching parent SK:", error);
      let errorMessage = "Gagal mengambil data SK rekomendasi dari perusahaan induk";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      setParentSkList([]);
      setParentCompanyName("");
    } finally {
      setIsLoadingParentSk(false);
    }
  }, [selectedCompany]);

  // Open SK modal
  const handleOpenSkModal = async () => {
    if (!selectedCompany) {
      toast.error("Silakan pilih perusahaan terlebih dahulu");
      return;
    }

    if (!hasPerusahaanInduk) {
      toast.error("Perusahaan ini tidak memiliki perusahaan induk");
      return;
    }

    onSkModalOpen();
    await fetchParentSkList();
  };

  // Filter SK list based on search query (client-side)
  const filteredSkList = useMemo(() => {
    // Ensure parentSkList is an array
    if (!Array.isArray(parentSkList)) return [];
    if (!skSearchQuery) return parentSkList;

    const query = skSearchQuery.toLowerCase();
    return parentSkList.filter((sk) => {
      // Safety check for sk object
      if (!sk || typeof sk !== "object" || sk.error || sk.statusCode) return false;

      const nomorSurat = sk.draftSurat?.nomorSuratFormatted?.toLowerCase() || "";
      const kodePermohonan = sk.kodePermohonan?.toLowerCase() || "";
      const tipeSurat = sk.tipeSurat?.toLowerCase() || "";

      return nomorSurat.includes(query) || kodePermohonan.includes(query) || tipeSurat.includes(query);
    });
  }, [parentSkList, skSearchQuery]);

  // Handle SK checkbox toggle
  const handleSkToggle = (skId, isAlreadyLinked) => {
    if (isAlreadyLinked) {
      toast.error("SK rekomendasi ini sudah terhubung dengan perusahaan Anda");
      return;
    }

    setSelectedSkIds((prev) => (prev.includes(skId) ? prev.filter((id) => id !== skId) : [...prev, skId]));
  };

  // Handle confirm link SK
  const handleConfirmLinkSk = async () => {
    if (selectedSkIds.length === 0) {
      toast.error("Silakan pilih SK rekomendasi terlebih dahulu");
      return;
    }
    onSkModalClose();
    onConfirmOpen();
  };

  // Actually link the SK
  const handleLinkSk = async () => {
    if (!selectedCompany) {
      toast.error("Silakan pilih perusahaan terlebih dahulu");
      return;
    }

    setIsLinkingSk(true);
    try {
      const response = await postFetcher("/api/rekom/permohonan/link-sk", {
        applicationIds: selectedSkIds,
        companyId: selectedCompany.value,
      });
      toast.success(response.message || "Berhasil menghubungkan SK rekomendasi");
      onConfirmClose();
      mutate();
      setSelectedSkIds([]);
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Gagal menghubungkan SK rekomendasi");
      }
    } finally {
      setIsLinkingSk(false);
    }
  };

  // Get selected SK details for confirmation
  const selectedSkDetails = useMemo(() => {
    return parentSkList.filter((sk) => selectedSkIds.includes(sk.id));
  }, [parentSkList, selectedSkIds]);

  return (
    <RootAdmin>
      <Card className="mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Daftar Permohonan Rekomendasi</p>
        </CardHeader>
        <Divider />
        <CardBody className="w-full h-[550px] p-5">
          <div className="w-full flex justify-between pb-3">
            <div className="w-1/4">
              <FilterReactSelect defaultValue={selectedCompany} isLoading={isLoadingCompany} options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} setValue={setSelectedCompany} placeholder="Pilih Perusahaan" />
            </div>
            {/* Button to update SK list for SIMPEL companies */}
            {hasIdCompanySimpel && hasPerusahaanInduk && (
              <div className="flex items-center">
                <Button size="sm" color="secondary" startContent={<LinkIcon className="size-4 stroke-2" />} onClick={handleOpenSkModal}>
                  Update Daftar SK
                </Button>
              </div>
            )}
          </div>
          <div className="w-full h-full">
            <ClientDataGrid
              rows={data?.applications}
              isLoading={isLoading}
              columns={columns}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    tanggalSurat: false,
                    nomorSuratPerusahaan: false,
                    tanggalSuratPerusahaan: false,
                    nomorSuratRekomendasi: false,
                    tanggalSuratRekomendasi: false,
                    tanggalSelesai: false,
                    tanggalBerlaku: false,
                    tanggalBerakhir: false,
                    identitasPemohon: false,
                    jumlahKendaraan: false,
                    bahanB3: false,
                  },
                },
              }}
            />
          </div>
        </CardBody>
      </Card>

      {/* Modal for SK Selection */}
      <Modal isOpen={isSkModalOpen} onClose={onSkModalClose} size="4xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <p>Pilih SK Rekomendasi dari Perusahaan Induk</p>
            {parentCompanyName && <p className="text-sm font-normal text-ink-500">Perusahaan Induk: {parentCompanyName}</p>}
          </ModalHeader>
          <ModalBody>
            {/* Search input */}
            <div className="mb-4">
              <Input
                placeholder="Cari nomor surat, kode permohonan, atau tipe surat..."
                value={skSearchQuery}
                onChange={(e) => setSkSearchQuery(e.target.value)}
                startContent={<MagnifyingGlassIcon className="size-4 text-ink-400" />}
                size="sm"
                isClearable
                onClear={() => setSkSearchQuery("")}
              />
            </div>

            {/* SK List */}
            {isLoadingParentSk ? (
              <div className="flex justify-center items-center py-10">
                <Spinner size="lg" />
              </div>
            ) : filteredSkList.length === 0 ? (
              <div className="text-center py-10 text-ink-500">{parentSkList.length === 0 ? "Tidak ada SK rekomendasi ditemukan dari perusahaan induk" : "Tidak ada hasil pencarian"}</div>
            ) : (
              <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-3 sticky top-0 z-10">
                    <tr>
                      <th className="p-3 text-left w-12 bg-surface-3"></th>
                      <th className="p-3 text-left bg-surface-3">Nomor Surat</th>
                      <th className="p-3 text-left bg-surface-3">Kode Permohonan</th>
                      <th className="p-3 text-left bg-surface-3">Tipe Surat</th>
                      <th className="p-3 text-left bg-surface-3">Status</th>
                      <th className="p-3 text-left bg-surface-3">Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSkList.map((sk, index) => (
                      <tr
                        key={sk.id}
                        className={`border-t ${sk.isLinkedToCurrentCompany ? "bg-surface-2 opacity-60" : "hover:bg-surface-2 cursor-pointer"}`}
                        onClick={() => !sk.isLinkedToCurrentCompany && handleSkToggle(sk.id, sk.isLinkedToCurrentCompany)}
                      >
                        <td className="p-3">
                          <Checkbox isSelected={selectedSkIds.includes(sk.id)} isDisabled={sk.isLinkedToCurrentCompany} onChange={() => handleSkToggle(sk.id, sk.isLinkedToCurrentCompany)} />
                        </td>
                        <td className="p-3">{sk.nomorSuratRekomendasi || "-"}</td>
                        <td className="p-3">{sk.kodePermohonan || "-"}</td>
                        <td className="p-3">{sk.tipeSurat || "-"}</td>
                        <td className="p-3">
                          <Chip size="sm" color="primary" variant="flat">
                            {sk.status}
                          </Chip>
                        </td>
                        <td className="p-3">
                          {sk.isLinkedToCurrentCompany && (
                            <Chip size="sm" color="success" variant="flat">
                              Sudah Terhubung
                            </Chip>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onSkModalClose}>
              Batal
            </Button>
            <Button color="primary" onPress={handleConfirmLinkSk} isDisabled={selectedSkIds.length === 0}>
              Hubungkan ({selectedSkIds.length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} size="2xl">
        <ModalContent>
          <ModalHeader>Konfirmasi Hubungkan SK Rekomendasi</ModalHeader>
          <ModalBody>
            <p className="mb-4">Anda akan menghubungkan {selectedSkIds.length} SK rekomendasi berikut ke perusahaan Anda:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedSkDetails.map((sk) => (
                <div key={sk.id} className="p-3 bg-surface-2 rounded-lg">
                  <div className="font-semibold">{sk.draftSurat?.nomorSuratFormatted || "-"}</div>
                  <div className="text-sm text-ink-500">{sk.kodePermohonan}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-ink-500">Apakah Anda yakin ingin melanjutkan?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onConfirmClose} isDisabled={isLinkingSk}>
              Batal
            </Button>
            <Button color="primary" onPress={handleLinkSk} isLoading={isLinkingSk}>
              Ya, Hubungkan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
